import {
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { PlanogramLayout } from '../../models/layout/planogram-layout';
import { animate, style, transition, trigger } from '@angular/animations';
import { ZoomService } from '../../services/zoom.service';
import { PlanogramService } from '../../services/planogram.service';
import { PlanogramApiService } from '../../services/planogram-api.service';
import { SignalRService } from '../../services/signalr.service';
import { CdkDropListGroup } from '@angular/cdk/drag-drop';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { LayoutService } from '../../services/layout.service';
import { ProductInspectorService } from '../../services/product-inspector.service';
import { VisualAnalysisService } from '../../services/visualanalysis.service';
import { OverlayService, OverlayType } from '../../services/overlay.service';
import { PlanogramStatusType } from '../../models/planogram-status';
import { map, Observable, Subject, takeUntil, timer } from 'rxjs';
import { ToastService } from '../../services/toasts.service';
import { SystemRole } from 'src/app/models/roles';
import { RolesService } from 'src/app/services/roles.service';
import { EventsService } from 'src/app/services/events.service';
import { EventType } from 'src/app/models/events';
import { ActivatedRoute, Router } from '@angular/router';
import { CapabilityType } from '../../models/capability-type';
import { ProductBinComponent } from '../../components/planogram/layout/product-bin/product-bin.component';
import { PlanogramManualLayoutComponent } from '../../components/planogram/layout/planogram-manual-layout/planogram-manual-layout.component';
import { PlanogramToolboxComponent } from '../../components/planogram/planogram-toolbox/planogram-toolbox.component';
import { VisualAnalysisOverlayComponent } from '../../components/planogram/visual-analysis-overlay/visual-analysis-overlay.component';
import { FloatingShelfComponent } from '../../components/planogram/floating-shelf/floating-shelf.component';
import { AsyncPipe } from '@angular/common';

const MISSING_DIMENSIONS_WARNING = 'missing-dimensions';

@Component({
    selector: 'app-planogram-page',
    templateUrl: './planogram.component.html',
    styleUrls: ['./planogram.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [CdkDropListGroup],
    animations: [
        trigger('fadeInOut', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('300ms ease-in', style({ opacity: 1 })),
            ]),
            transition(':leave', [animate('300ms ease-out', style({ opacity: 0 }))]),
        ]),
    ],
    imports: [
        CdkDropListGroup,
        ProductBinComponent,
        PlanogramManualLayoutComponent,
        PlanogramToolboxComponent,
        VisualAnalysisOverlayComponent,
        FloatingShelfComponent,
        AsyncPipe,
    ],
})
export class PlanogramComponent implements OnInit, OnDestroy {
  CapabilityType = CapabilityType;
  layout: PlanogramLayout | null = null;
  planogramZoom: number = 4;
  showLoadingSpinner: boolean = false;

  /** True whenever an overlay legend is showing (any overlay type but NONE),
   *  used to reserve right-side space so the fixed legend doesn't cover the
   *  planogram drops. */
  legendOpen$!: Observable<boolean>;

  private hasShownMissingDimensionsWarning: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    private planogramService: PlanogramService,
    private layoutService: LayoutService,
    private planogramApiService: PlanogramApiService,
    private signalRService: SignalRService,
    private zoomService: ZoomService,
    private rolesService: RolesService,
    private eventsService: EventsService,
    public productInspectorService: ProductInspectorService,
    public visualAnalysisService: VisualAnalysisService,
    private overlayService: OverlayService,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private router: Router,
    private offcanvasService: NgbOffcanvas
  ) {
    this.legendOpen$ = this.overlayService.overlayType$.pipe(
      map((type) => type !== OverlayType.NONE)
    );
  }

  @HostBinding('class.loading')
  get isLoadingClass() {
    return this.showLoadingSpinner;
  }

  ngOnInit() {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(async (params) => {
      const routePlanogramId = params['id'];
      const routeStateId = params['stateId'] || null;

      if (!routePlanogramId) {
        this.router.navigate(['/']);
        return;
      }

      const planogramId = this.planogramService.getPlanogram();
      const planogramStateId = this.planogramService.getPlanogramState();

      // Only fetch from API if the service doesn't already have the data for this specific state
      if (planogramId !== routePlanogramId || planogramStateId !== routeStateId) {
        this.hasShownMissingDimensionsWarning = false;
        try {
          const options = await this.planogramApiService.loadPlanogram(
            routePlanogramId,
            routeStateId
          );
          this.planogramService.configureFromParams(
            routePlanogramId,
            routeStateId,
            options
          );
        } catch (error) {
          console.error('Failed to load planogram', error);
          this.router.navigate(['/']);
          return;
        }
      }
      
      // Reset layout before re-initializing logic to prevent ghosting
      this.initializePlanogramLogic();
    });

    this.layoutService.layout$.pipe(takeUntil(this.destroy$)).subscribe((layout) => {
      this.layout = layout;
      if (!layout) {
        this.toastService.clearPersistentWarning(MISSING_DIMENSIONS_WARNING);
        return;
      }
      const hasInvalid = Array.from(layout.productReference.values()).some(
        (prod) =>
          !prod.dimensions ||
          prod.dimensions.width <= 0 ||
          prod.dimensions.height <= 0 ||
          prod.dimensions.depth <= 0
      );
      if (hasInvalid && !this.hasShownMissingDimensionsWarning) {
        let invalidProducts = Array.from(layout.productReference.values())
          .filter(
            (prod) =>
              !prod.dimensions ||
              prod.dimensions.width <= 0 ||
              prod.dimensions.height <= 0 ||
              prod.dimensions.depth <= 0
          )
          .map((p) => p.description);
        this.toastService.showPersistentWarning(
          'Missing Dimensions',
          `These products have missing or zero dimensions:<br>${invalidProducts.join(
            '<br>'
          )}`,
          MISSING_DIMENSIONS_WARNING
        );
        this.hasShownMissingDimensionsWarning = true;
      } else if (!hasInvalid) {
        this.toastService.clearPersistentWarning(MISSING_DIMENSIONS_WARNING);
        this.hasShownMissingDimensionsWarning = false;
      }
    });
  }

  private initializePlanogramLogic() {
    this.zoomService.zoom$.pipe(takeUntil(this.destroy$)).subscribe((zoom) => {
      this.planogramZoom = zoom;
    });

    this.planogramService.status$
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        if (status.state === PlanogramStatusType.GENERATING) {
          timer(500)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              if (
                this.planogramService.getStatus()?.state ===
                PlanogramStatusType.GENERATING
              ) {
                this.showLoadingSpinner = true;
              }
            });
        } else {
          this.showLoadingSpinner = false;
        }
      });

    // Track planogram viewed - only for EXTERNAL users
    let planogramId = this.planogramService.getPlanogram();
    if (
      planogramId &&
      this.rolesService.roles.value.includes(SystemRole.EXTERNAL)
    ) {
      this.eventsService
        .createEvent({
          planogramId,
          eventType: EventType.PLANOGRAM_VIEWED,
          description: 'User viewed the planogram',
        })
        .subscribe();
    }

    let stateId = this.planogramService.getPlanogramState();
    if (stateId) {
      this.signalRService.loadPlanogramState(stateId);
    } else {
      this.signalRService.generatePlanogram();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.visualAnalysisService.isOpen) {
      this.visualAnalysisService.setOpen(false);
    }
    if (this.offcanvasService.hasOpenOffcanvas()) {
      this.offcanvasService.dismiss();
    }

    this.overlayService.clearOverlay();
    
    // Explicitly clear the warning toast when leaving the page
    this.toastService.clearPersistentWarning(MISSING_DIMENSIONS_WARNING);

    this.layoutService.clearLayout();
    this.planogramService.clearPlanogram();
    this.productInspectorService.close();
  }

  setZoom($event: any) {
    if (typeof $event.target == 'undefined' || $event.target == null) {
      return;
    }
    let newZoom: number = $event.target.value;
    if (newZoom >= 2 && newZoom <= 6) {
      this.planogramZoom = newZoom;
    }
  }
}